import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { useState, useEffect } from "react";
import useAuth from "@/hooks/auth";
import useActivityTracker from "@/hooks/useActivityTracker";
import { submitGuideRequest } from "@/services/api/GuideRequest";
import toast from "react-hot-toast";

const TOPIC_OPTIONS = [
  "Fundraising",
  "Investor outreach",
  "Pitch deck",
  "Hiring",
  "Growth",
  "Legal/Terms",
  "Using Lucidity",
  "Other",
];

export function RequestGuide({ open, onOpenChange }) {
  const { user } = useAuth();
  const { trackModal, trackFormSubmission } = useActivityTracker();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [topic, setTopic] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (open) {
      trackModal("founder_library_request", "opened");
    }
  }, [open]);

  useEffect(() => {
    if (open && user?.email) {
      setEmail(user.email);
    }
  }, [open, user]);

  const resetForm = () => {
    setMessage("");
    setTopic("");
    setEmail(user?.email || "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        message: message.trim(),
      };

      if (topic) {
        payload.topic = topic;
      }

      if (!user && email.trim()) {
        payload.email = email.trim();
      }

      const response = await submitGuideRequest(payload);

      toast.success(
        response?.data?.message ||
          "Thanks — we'll review and prioritize based on demand."
      );

      trackFormSubmission("founder_library_request_submitted", {
        topic: topic || "none",
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      const status = error?.response?.status;

      if (status === 422) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          const firstError = Object.values(errors)[0];
          toast.error(Array.isArray(firstError) ? firstError[0] : firstError);
        } else {
          toast.error(
            error?.response?.data?.message || "Please check your input."
          );
        }
      } else {
        toast.error("Something went wrong — please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='bg-white sm:max-w-sm text-background'>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Request Guides</DialogTitle>
            <DialogDescription className='mt-2 text-sm'>
              {`Let us know what guides you'd like to see next!`}
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <Label htmlFor='need'>
                What do you need? <span className='text-red-500'>*</span>
              </Label>
              <Textarea
                className='bg-white text-background border-black/15'
                rows={3}
                id='need'
                name='need'
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor='topic'>Topic</FieldLabel>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger
                  id='topic'
                  className='bg-white text-background border-black/15'
                >
                  <SelectValue placeholder='Select a topic' />
                </SelectTrigger>
                <SelectContent className='bg-white text-background border-black/15'>
                  <SelectGroup>
                    {TOPIC_OPTIONS.map((option) => (
                      <SelectItem
                        key={option}
                        className='hover:bg-background/5'
                        value={option}
                      >
                        {option}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            {!user && (
              <Field>
                <Label htmlFor='email'>Email</Label>
                <Input
                  className='bg-white text-background border-black/15'
                  id='email'
                  name='email'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
            )}
          </FieldGroup>
          <DialogFooter className='mt-4'>
            <DialogClose asChild>
              <Button
                type='button'
                variant='outline'
                className='text-black bg-transparent border-black/10 hover:bg-black/5'
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type='submit'
              className='text-white bg-black hover:bg-gray-900'
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
